"use client";

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/providers/auth-provider";

export interface DashboardKPI {
  label: string;
  value: string;
  subtext?: string;
  color: string;
  href?: string;
}

export interface CAMonthPoint {
  month: string;
  ca: number;
}

export interface RelanceItem {
  id: string;
  ref: string;
  client: string;
  amount: number;
  jours: number;
}

export interface ProspectItem {
  id: string;
  name: string;
  nbDevis: number;
}

export interface TopClientItem {
  name: string;
  ca: number;
}

export interface DashboardData {
  kpis: DashboardKPI[];
  caMonthly: CAMonthPoint[];
  topClients: TopClientItem[];
  relances: RelanceItem[];
  prospects: ProspectItem[];
  counters: { acceptes: number; factures: number };
  marge: { percent: number; euros: number };
}

/**
 * Dashboard data hook — single server-side call.
 * All pagination + computation happens in /api/dashboard (server-to-server).
 * Client makes ONE request instead of 150+.
 */
export function useDashboardData(userId?: number | null) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["dashboard-data-v3", userId ?? "all"],
    queryFn: async (): Promise<DashboardData> => {
      const apiKey = typeof window !== "undefined" ? localStorage.getItem("dolapikey") : null;
      if (!apiKey) throw new Error("Non authentifié");

      const params = new URLSearchParams();
      if (userId) params.set("userId", String(userId));

      const res = await fetch(`/api/dashboard?${params.toString()}`, {
        headers: { dolapikey: apiKey },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Erreur serveur" }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      return res.json();
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}
